import toMillis from './toMillis'
import resolveStartDate from './resolveStartDate'
import momentCompletionStatus from './momentCompletionStatus'
import { TRAJECT_END_STATUSES } from '../statusHistory/computeTrajectDuration'
import {
    OVERDUE_FACTOR,
    DUE_SOON_FACTOR,
    BASELINE_GRACE_DAYS,
    HOUSEHOLD_FOLLOW_UP_DAYS,
    HOUSEHOLD_DUE_SOON_DAYS,
} from './thresholds'

const DAY_MS = 24 * 60 * 60 * 1000

/** Statussen waarin een deelnemer niet meer gesignaleerd hoeft te worden. */
const INACTIVE_PERSONA_STATUSES = ['inactive', 'outflow']

/**
 * @typedef {object} SignalEntity
 * @property {string} ID - Personas.ID of Adresses.ID
 * @property {'persona'|'adress'} kind - Welke soort; bepaalt de urgentieregel
 * @property {string} name - Wat de gebruiker ziet
 * @property {string} status - Personas.Status of Adresses.Status
 * @property {Array<string>} researchIds - De EFFECTIEVE set Research.ID's: handmatig gekoppeld
 *   plus elk onderzoek dat via de schakelaar voor deze soort geldt (`effectiveResearchIds`)
 * @property {*} [StartDate] - Alleen deelnemers; tekst of tijdstempel
 * @property {*} [Timestamp] - Aanmaakmoment, laatste terugval voor de startdatum
 *
 * @typedef {object} SignalMoment
 * @property {string} ID
 * @property {string} Title
 * @property {number} Position
 * @property {'filled'|'missing'|'missed'|'unknown'} status
 * @property {number|null} filledAtMs
 * @property {number|null} createdAtMs - Wanneer het meetmoment is aangemaakt
 *
 * @typedef {object} MeasurementSignalRow
 * @property {string} key - `${entityId}|${researchId}`
 * @property {string} entityId
 * @property {'persona'|'adress'} entityKind
 * @property {string} entityName
 * @property {boolean} active - `false` voor uitgestroomde of inactieve deelnemers
 * @property {string} researchId
 * @property {string} researchTitle
 * @property {string} questionnaireId - De vragenlijst van het onderzoek; voedt de deel-link
 * @property {Array<SignalMoment>} moments - Op volgorde; voedt de chips-rij
 * @property {SignalMoment|null} next - Het eerstvolgende niet-ingevulde meetmoment
 * @property {boolean} hasGap - Een later meetmoment is al ingevuld, een eerder niet
 * @property {boolean} missedBaseline - De nulmeting kon niet meer: de deelnemer was al
 *   onderweg toen dit meetmoment werd aangemaakt
 * @property {number|null} startedAtMs
 * @property {boolean} startEstimated
 * @property {'startDate'|'statusHistory'|'created'|null} startSource
 * @property {number|null} openSinceMs - Sinds wanneer `next` openstaat
 * @property {number|null} openDays - Hoe lang, in hele dagen; nooit negatief
 * @property {number|null} intervalDays - De afgeleide tussentijd van dit onderzoek
 * @property {number|null} dueAtMs - De datum waarop `next` aan de beurt is. Alleen bij de
 *   laatste meting van een huishouden waarvan het traject is afgerond: zes weken na die
 *   afronding. Overal anders `null` — een verzonnen datum is erger dan geen datum
 * @property {'complete'|'overdue'|'due'|'upcoming'|'open'|'waiting'|'unknown'} urgency
 * @property {'time'|'traject-completed'|'traject-running'|null} reason
 */

/**
 * Wanneer ging dit huishouden voor het laatst op 'Afgerond'?
 *
 * De LAATSTE afronding telt, niet de eerste: een heropend en opnieuw afgerond
 * traject is pas bij die tweede afronding klaar — zelfde regel als
 * `computeTrajectDuration`. Rijen van andere entiteiten zitten in dezelfde
 * lijst en tellen niet mee.
 *
 * @param {Array<object>} statusEntries
 * @param {string} entityId
 * @returns {number|null} Millis, of `null` als de afronding nooit is vastgelegd
 */
const lastCompletionMs = (statusEntries, entityId) => {
    const closing = TRAJECT_END_STATUSES.adress
    let latest = null
    ;(statusEntries || []).forEach((entry) => {
        if (!entry || entry.Deleted || entry.EntityID !== entityId) return
        if (!closing.includes(entry.ToStatus)) return
        const ms = toMillis(entry.ChangedAt)
        if (ms !== null && (latest === null || ms > latest)) latest = ms
    })
    return latest
}

/**
 * Zet de losse gegevens om in één rij per deelnemer/huishouden × onderzoek:
 * wat staat er open, hoe lang al, en hoe dringend is dat.
 *
 * Er is niets in te stellen — alles volgt uit wat er al staat. Het eerstvolgende
 * niet-ingevulde meetmoment is wat openstaat; hoe lang dat zo is telt vanaf de
 * vorige meting, of vanaf de startdatum zolang er nog geen meting is. Een
 * "te laat"-oordeel valt alleen als het onderzoek een eigen tussentijd heeft
 * laten zien (zie `inferIntervalDays`); anders blijft het bij het feit.
 *
 * Huishoudens volgen voor hun laatste meetmoment een vaste regel: dat is aan
 * de beurt zes weken nadat het traject op 'Afgerond' is gezet (Gijs,
 * 2026-09-11). Zolang het traject loopt is er geen datum ('Traject loopt nog');
 * is de afronding nooit vastgelegd in StatusHistory, dan weten we wél dat het
 * klaar is maar niet sinds wanneer, en zegt de rij dat. Eerdere meetmomenten
 * van een huishouden volgen de gewone regel. Zie `lastCompletionMs`.
 *
 * @param {object} [args]
 * @param {Array<SignalEntity>} [args.entities]
 * @param {Record<string, {ID: string, Title?: string, QuestionnaireID?: string}>} [args.researchById]
 * @param {Record<string, Array<{ID: string, Title?: string, Position?: number}>>} [args.momentsByResearch]
 * @param {Array<{respondentId: string, momentId: string, researchId: string, filledAtMs: number|null}>} [args.fills]
 * @param {Record<string, boolean>} [args.anonymousByMoment] - Per meetmoment: staan er
 *   naamloze antwoorden op? Zo ja, dan kan het antwoord van iemand daartussen schuilgaan
 *   en is afwezigheid geen zekerheid.
 * @param {Record<string, number|null>} [args.intervalByResearch] - Afgeleide tussentijd per onderzoek
 * @param {Array<object>} [args.statusEntries] - StatusHistory, voor de startdatum
 * @param {number} [args.now] - Nu, in milliseconden
 * @returns {Array<MeasurementSignalRow>}
 */
const buildMeasurementSignals = ({
    entities,
    researchById,
    momentsByResearch,
    fills,
    anonymousByMoment,
    intervalByResearch,
    statusEntries,
    now,
} = {}) => {
    const researches = researchById || {}
    const momentsByResearchId = momentsByResearch || {}
    const anonymous = anonymousByMoment || {}
    const intervals = intervalByResearch || {}
    const nowMs = typeof now === 'number' ? now : Date.now()

    // Eén index van wie wat wanneer invulde. De vroegste invulling telt: een
    // herziening later verandert niet wanneer het meetmoment aan de beurt was.
    /** @type {Record<string, number|null>} */
    const filledAt = {}
    ;(fills || []).forEach((entry) => {
        if (!entry || !entry.respondentId || !entry.momentId) return
        const key = `${entry.respondentId}|${entry.momentId}`
        const ms = typeof entry.filledAtMs === 'number' ? entry.filledAtMs : null
        if (!(key in filledAt) || (ms !== null && (filledAt[key] === null || ms < filledAt[key]))) {
            filledAt[key] = ms
        }
    })

    const rows = []

    ;(entities || []).forEach((entity) => {
        if (!entity || !entity.ID) return

        const start = resolveStartDate({ entity, statusEntries })
        const active = entity.kind === 'persona'
            ? !INACTIVE_PERSONA_STATUSES.includes(entity.status)
            : true

        ;(entity.researchIds || []).forEach((researchId) => {
            const research = researches[researchId]
            if (!research) return

            const rawMoments = momentsByResearchId[researchId]
            if (!Array.isArray(rawMoments) || rawMoments.length === 0) return

            // Een meetmoment zonder Position achteraan, niet weglaten — een
            // ontbrekend sorteerveld mag geen rij doen verdwijnen.
            const ordered = [...rawMoments].sort(
                (a, b) => (a?.Position ?? Number.MAX_SAFE_INTEGER) - (b?.Position ?? Number.MAX_SAFE_INTEGER)
            )

            const moments = ordered.map((moment, index) => {
                const key = `${entity.ID}|${moment.ID}`
                const isFilled = key in filledAt
                const couldBeHidden = moment.ID in anonymous ? anonymous[moment.ID] : null
                const createdAtMs = toMillis(moment.Timestamp)

                // Een nulmeting meet de beginsituatie en kan dus alleen aan het
                // begin van een traject. Wie al meedraaide toen het meetmoment
                // werd aangemaakt, kan hem niet meer inhalen: dat is geen
                // achterstand maar een gepasseerd station. Alleen het eerste
                // meetmoment kent deze regel — een tussen- of nameting is bij
                // een lopend traject nog gewoon te doen.
                const missedBaseline = index === 0
                    && !isFilled
                    && start !== null
                    && createdAtMs !== null
                    && start.ms < createdAtMs - BASELINE_GRACE_DAYS * DAY_MS

                return {
                    ID: moment.ID,
                    Title: moment.Title || '',
                    Position: moment.Position ?? null,
                    status: missedBaseline
                        ? 'missed'
                        : momentCompletionStatus({ filled: isFilled, couldBeHidden }),
                    filledAtMs: isFilled ? filledAt[key] : null,
                    createdAtMs,
                }
            })

            // Een gemiste nulmeting blokkeert niets: het eerstvolgende meetmoment
            // dat nog te doen valt, is wat openstaat.
            const nextIndex = moments.findIndex(
                (moment) => moment.status !== 'filled' && moment.status !== 'missed'
            )
            const next = nextIndex === -1 ? null : moments[nextIndex]
            const hasGap = nextIndex !== -1
                && moments.slice(nextIndex + 1).some((moment) => moment.status === 'filled')
            const missedBaselineCount = moments.filter((moment) => moment.status === 'missed').length

            // Sinds wanneer staat dit open: sinds de vorige meting, of — zolang
            // er nog geen meting is — sinds de start.
            //
            // Begrensd door het moment waarop het meetmoment werd aangemaakt.
            // Een deelnemer die in 2010 begon in een onderzoek van 2025 heeft
            // de nulmeting niet zestien jaar laten liggen; die kón toen niet.
            // Bleek pas bij het bekijken van de echte app.
            const previousFilledAt = nextIndex > 0 ? moments[nextIndex - 1].filledAtMs : null
            const anchorMs = previousFilledAt ?? (start ? start.ms : null)
            const openSinceMs = next === null
                ? null
                : (anchorMs === null
                    ? null
                    : Math.max(anchorMs, next.createdAtMs ?? anchorMs))

            const openDays = openSinceMs === null
                ? null
                : Math.max(0, Math.floor((nowMs - openSinceMs) / DAY_MS))

            const intervalDays = intervals[researchId] ?? null
            const isLastMoment = next !== null && nextIndex === moments.length - 1
            const householdRule = entity.kind === 'adress' && isLastMoment
            const trajectCompleted = TRAJECT_END_STATUSES.adress.includes(entity.status)

            // Zes weken na de afronding van het traject — en alleen dan. Een
            // lopend traject heeft geen datum, en een afronding die nooit is
            // vastgelegd ook niet.
            const completedAtMs = householdRule && trajectCompleted
                ? lastCompletionMs(statusEntries, entity.ID)
                : null
            const dueAtMs = completedAtMs === null
                ? null
                : completedAtMs + HOUSEHOLD_FOLLOW_UP_DAYS * DAY_MS

            let urgency
            let reason = null

            if (next === null) {
                urgency = 'complete'
            } else if (next.status === 'unknown') {
                // Kan het antwoord tussen de naamloze antwoorden zitten, dan is
                // elk oordeel een gok.
                urgency = 'unknown'
            } else if (householdRule && !trajectCompleted) {
                urgency = 'waiting'
                reason = 'traject-running'
            } else if (householdRule && dueAtMs === null) {
                // Afgerond, maar zonder vastgelegde datum: wel melden, niet oordelen.
                urgency = 'open'
                reason = 'traject-completed'
            } else if (householdRule) {
                reason = 'traject-completed'
                if (nowMs >= dueAtMs) {
                    urgency = 'overdue'
                } else if (nowMs >= dueAtMs - HOUSEHOLD_DUE_SOON_DAYS * DAY_MS) {
                    urgency = 'due'
                } else {
                    urgency = 'upcoming'
                }
            } else if (openDays === null || intervalDays === null) {
                // Geen ankerpunt of geen eigen tussentijd: wel melden, niet oordelen.
                urgency = 'open'
                reason = 'time'
            } else if (openDays >= intervalDays * OVERDUE_FACTOR) {
                urgency = 'overdue'
                reason = 'time'
            } else if (openDays >= intervalDays * DUE_SOON_FACTOR) {
                urgency = 'due'
                reason = 'time'
            } else {
                urgency = 'upcoming'
                reason = 'time'
            }

            rows.push({
                key: `${entity.ID}|${researchId}`,
                entityId: entity.ID,
                entityKind: entity.kind,
                entityName: entity.name || '',
                active,
                researchId,
                researchTitle: research.Title || '',
                questionnaireId: research.QuestionnaireID || '',
                moments,
                next,
                hasGap,
                missedBaseline: missedBaselineCount > 0,
                startedAtMs: start ? start.ms : null,
                startEstimated: start ? start.estimated : false,
                startSource: start ? start.source : null,
                openSinceMs,
                openDays,
                intervalDays,
                dueAtMs,
                urgency,
                reason,
            })
        })
    })

    return rows
}

export default buildMeasurementSignals
export { INACTIVE_PERSONA_STATUSES }
